import { useState } from 'react';
import { FileText, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { BRIEF_FIELDS, BRIEF_STATUS_META, deliverableSummary } from '../lib/briefHelpers';

// A brief's status as a pill: a 6px currentColor dot before the label, so the
// status is never told apart by colour alone. Colours come from
// BRIEF_STATUS_META in lib/briefHelpers. Shared by BriefsPage and
// BriefDetailPage, so the pill lives in one place.
export function BriefStatusBadge({ status, className = '' }) {
  const meta = BRIEF_STATUS_META[status] || BRIEF_STATUS_META.draft;
  return (
    <Badge className={cn('gap-1.5', meta.className, className)} variant="secondary">
      <span className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" aria-hidden="true" />
      {meta.label}
    </Badge>
  );
}

// Read-only reference panel for a brief. Used above the job description while quoting.
export function BriefQuickView({ brief, onOpen }) {
  const [open, setOpen] = useState(true);
  if (!brief) return null;
  const filledFields = BRIEF_FIELDS.filter((f) => brief[f.key]);

  return (
    <div className="rounded-tile bg-surface overflow-hidden" data-testid="brief-quick-view">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 border-b border-line bg-mist text-left"
        data-testid="brief-quick-view-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm truncate">Brief: {brief.project_name}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <BriefStatusBadge status={brief.status} />
          {onOpen && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onOpen(); } }}
              className="text-xs text-clay hover:underline flex items-center gap-1 cursor-pointer"
              data-testid="brief-quick-view-open"
            >
              Open <ExternalLink className="w-3 h-3" />
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="p-4 space-y-3 max-h-[520px] overflow-y-auto text-sm">
          {brief.client && (
            <p className="text-muted-foreground"><span className="font-medium text-foreground">Client:</span> {brief.client}</p>
          )}
          {Array.isArray(brief.shoot_dates) && brief.shoot_dates.length > 0 && (
            <p className="text-muted-foreground"><span className="font-medium text-foreground">Shoot dates:</span> {brief.shoot_dates.join(', ')}</p>
          )}

          {filledFields.map((f) => (
            <div key={f.key}>
              <p className="font-medium text-foreground">{f.label}</p>
              <p className="text-muted-foreground whitespace-pre-wrap">{brief[f.key]}</p>
            </div>
          ))}

          {Array.isArray(brief.deliverables) && brief.deliverables.length > 0 && (
            <div>
              <p className="font-medium text-foreground mb-1">Deliverables ({brief.deliverables.length})</p>
              <ul className="space-y-1">
                {brief.deliverables.map((d) => (
                  <li key={d.id} className="text-muted-foreground flex items-start gap-1">
                    <span className="text-ink-faded">•</span>
                    <span>
                      {deliverableSummary(d) || 'Untitled deliverable'}
                      {d.quantity > 1 ? ` (x${d.quantity})` : ''}
                      {d.notes ? ` — ${d.notes}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {filledFields.length === 0 && (!brief.deliverables || brief.deliverables.length === 0) && (
            <p className="text-muted-foreground italic">No brief details captured yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
